import { z } from "zod";
import { ExtentSchema } from "./map";
import {
  AssetVersionIdSchema,
  DescriptionSchema,
  HexColorSchema,
  IdSchema,
  listOf,
  NameSchema,
} from "./primitives";

export const SpectatorRefSchema = z.strictObject({ type: z.literal("spectator") });
export const PerspectiveHandleRefSchema = z.strictObject({
  type: z.literal("perspective"),
  handle: IdSchema,
});

/** One view: the spectator or a single perspective. */
export const ViewRefSchema = z.discriminatedUnion("type", [
  SpectatorRefSchema,
  PerspectiveHandleRefSchema,
]);
export type ViewRef = z.infer<typeof ViewRefSchema>;

export const PerspectiveSchema = z.strictObject({
  handle: IdSchema.meta({
    description:
      "Identifies the perspective everywhere, its own view and others alike. Random, never reused, and it cannot change.",
  }),
  premise: DescriptionSchema,
  startingView: ExtentSchema.optional().meta({
    description: "Where the map opens for its players.",
  }),
});
export type Perspective = z.infer<typeof PerspectiveSchema>;

/**
 * Something the lobby offers to play as. The game master decides what asking for one becomes, and
 * takes it away once someone plays it.
 */
export const PerspectiveSuggestionSchema = z.strictObject({
  id: IdSchema,
  name: NameSchema,
  premise: DescriptionSchema,
  order: z.number().meta({ description: "Where it lists among suggestions, lowest first." }),
  recommended: z.boolean().optional().meta({
    description: "Listed ahead of the rest as recommended, in the same order.",
  }),
  icon: AssetVersionIdSchema.optional().meta({
    description: "The image asset version the perspective it becomes is shown by.",
  }),
  thumbnail: AssetVersionIdSchema.optional().meta({
    description: "An image asset version shown beside it in the lobby's list, and nowhere else.",
  }),
  color: HexColorSchema.optional(),
  shapeIds: listOf(IdSchema).optional().meta({
    description: "The shapes on the spectator's map it stands in, which the lobby outlines as one.",
  }),
  startingView: ExtentSchema.meta({
    description: "Where the map opens for a player who plays it.",
  }),
});
export type PerspectiveSuggestion = z.infer<typeof PerspectiveSuggestionSchema>;

/** A view to fork: the spectator, or a perspective by its handle. */
export const ForkSchema = z.discriminatedUnion("type", [
  SpectatorRefSchema,
  PerspectiveHandleRefSchema,
]);
export type Fork = z.infer<typeof ForkSchema>;

export const PerspectiveOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("perspective.set"),
    perspective: PerspectiveSchema,
    /** The view whose map deltas so far the new perspective receives a copy of. */
    fork: ForkSchema.optional(),
  }),
  z.strictObject({
    type: z.literal("perspective.update"),
    changes: z.strictObject({
      premise: DescriptionSchema.optional(),
      startingView: ExtentSchema.nullable().optional(),
    }),
    /** The event it is sent with, which it arrives as it plays. */
    eventId: IdSchema.optional(),
  }),
  z.strictObject({
    type: z.literal("perspective.updateUsers"),
    userIds: listOf(IdSchema).meta({ description: "Everyone on the perspective now." }),
  }),
  z.strictObject({ type: z.literal("perspective.unset") }),
]);
export type PerspectiveOperation = z.infer<typeof PerspectiveOperationSchema>;

/** How a view shows a perspective, its own included: by a name, an icon, and a colour. */
export const AliasSchema = z.strictObject({
  name: NameSchema,
  icon: AssetVersionIdSchema.optional(),
  color: HexColorSchema.optional(),
});
export type Alias = z.infer<typeof AliasSchema>;

/** A view with no alias for a perspective shows it as this. */
export const UNKNOWN_ALIAS_NAME = "Unknown perspective";

/** What an alias shows, or the unknown perspective when there is none. */
export function resolveAlias(alias: Alias | undefined): Alias {
  return alias ?? { name: UNKNOWN_ALIAS_NAME };
}

export const AliasOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("alias.set"),
    handle: IdSchema,
    alias: AliasSchema,
    /** The event it is sent with, which it arrives as it plays. */
    eventId: IdSchema.optional(),
  }),
  z.strictObject({
    type: z.literal("alias.unset"),
    handle: IdSchema,
    /** The event it is sent with, which it arrives as it plays. */
    eventId: IdSchema.optional(),
  }),
]);
export type AliasOperation = z.infer<typeof AliasOperationSchema>;

export const PerspectiveSuggestionOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("perspectiveSuggestion.set"),
    perspectiveSuggestion: PerspectiveSuggestionSchema,
  }),
  z.strictObject({
    type: z.literal("perspectiveSuggestion.update"),
    id: IdSchema,
    changes: z.strictObject({
      name: NameSchema.optional(),
      premise: DescriptionSchema.optional(),
      order: z.number().optional(),
      recommended: z.boolean().optional(),
      icon: AssetVersionIdSchema.nullable().optional(),
      thumbnail: AssetVersionIdSchema.nullable().optional(),
      color: HexColorSchema.nullable().optional(),
      shapeIds: listOf(IdSchema).nullable().optional(),
      startingView: ExtentSchema.optional(),
    }),
  }),
  z.strictObject({ type: z.literal("perspectiveSuggestion.unset"), id: IdSchema }),
]);
export type PerspectiveSuggestionOperation = z.infer<typeof PerspectiveSuggestionOperationSchema>;

/** A perspective a user describes for themselves. */
export const CustomPerspectiveSchema = PerspectiveSuggestionSchema.pick({
  name: true,
  premise: true,
  icon: true,
  color: true,
  shapeIds: true,
  startingView: true,
}).extend({ type: z.literal("custom") });
export type CustomPerspective = z.infer<typeof CustomPerspectiveSchema>;

/**
 * Where a user asks to be: watching as the spectator, an offered suggestion, their own, or an
 * existing perspective alongside its players.
 */
export const PerspectiveDestinationSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("spectator") }),
  z.strictObject({ type: z.literal("suggestion"), suggestionId: IdSchema }),
  CustomPerspectiveSchema,
  z.strictObject({ type: z.literal("perspective"), handle: IdSchema }),
]);
export type PerspectiveDestination = z.infer<typeof PerspectiveDestinationSchema>;

/**
 * A user's ask to change perspective, and the game master's answer. The user asks and may take
 * it back; the game master approves it once the user is where it put them, or refuses it.
 */
export const PerspectiveChangeOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("perspectiveChange.requested"),
    id: IdSchema,
    to: PerspectiveDestinationSchema,
  }),
  z.strictObject({ type: z.literal("perspectiveChange.withdrawn"), id: IdSchema }),
  z.strictObject({ type: z.literal("perspectiveChange.approved"), id: IdSchema }),
  z.strictObject({
    type: z.literal("perspectiveChange.refused"),
    id: IdSchema,
    reason: z.string().trim().min(1).max(2_000).meta({ description: "Shown to the user." }),
  }),
]);
export type PerspectiveChangeOperation = z.infer<typeof PerspectiveChangeOperationSchema>;

/** A user's ask to change perspective that the game master has not answered. */
export interface PerspectiveChangeRequest {
  id: string;
  userId: string;
  to: PerspectiveDestination;
  /** The revision that asked, which orders requests first come, first served. */
  revision: number;
}
